import React, { useRef, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import MobileChartWrapper from './MobileChartWrapper';
import MSTRMobileMainChartPage from './mstr/MSTRMobileMainChartPage';
import MobilePredictionsList from './MobilePredictionsList';
import MobileBottomNav, { FilterStatus } from './MobileBottomNav';
import BackToTopButton from './BackToTopButton';
import MobileInfluencerFilter from './MobileInfluencerFilter';
import { useLivePrice } from '../../hooks/useLivePrice';
import { Prediction } from '../../types/predictions';
import { Theme } from '../../theme';
import { formatPrediction } from '../../utils/priceUtils';

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px; /* Reduced from 1px to 0px - elements touch each other */
  width: 100%;
  height: 100vh;
  padding: 1px 8px 80px 1px; /* Reduced top padding from 2px to 1px */
  box-sizing: border-box; /* Ensure padding is included in width */
  overflow-x: hidden; /* Prevent horizontal overflow */
  
  /* Improved scroll behavior */
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  scroll-behavior: smooth;
  
  /* PWA mode - adjust for taller bottom nav */
  @media (display-mode: standalone) {
    padding-bottom: max(96px, 76px + env(safe-area-inset-bottom) + 20px);
  }
  
  /* Add minimum scrollable content to prevent scroll trap */
  &::after {
    content: '';
    height: 20px;
    width: 100%;
    flex-shrink: 0;
  }
`;

const PullToRefreshIndicator = styled.div<{ visible: boolean; pulling: boolean }>`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%) translateY(${props => props.visible ? '0' : '-100%'});
  background: ${props => props.theme.primary};
  color: white;
  padding: 8px 16px;
  border-radius: 0 0 8px 8px;
  font-size: 12px;
  font-weight: 600;
  z-index: 1000;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  
  ${props => props.pulling && `
    background: ${props.theme.primary};
    transform: translateX(-50%) translateY(0) scale(1.05);
  `}
`;

const AssetSelectionOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const AssetMenu = styled.div`
  background: ${props => props.theme.surface};
  border: 1px solid ${props => props.theme.border};
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 300px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const AssetMenuHeader = styled.h3`
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  color: ${props => props.theme.text};
`;

const AssetOption = styled.button<{ active: boolean; theme: any }>`
  width: 100%;
  padding: 16px;
  margin-bottom: 12px;
  background: ${props => props.active ? props.theme.primary : props.theme.background};
  color: ${props => props.active ? 'white' : props.theme.text};
  border: 1px solid ${props => props.active ? props.theme.primary : props.theme.border};
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  text-align: left;
  
  &:hover {
    background: ${props => props.active ? props.theme.primary : props.theme.border};
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const AssetMenuClose = styled.button`
  width: 100%;
  padding: 12px;
  background: transparent;
  color: ${props => props.theme.text};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  opacity: 0.7;
  
  &:hover {
    opacity: 1;
    background: ${props => props.theme.border};
  }
`;

const BitcoinIcon = styled.svg`
  width: 40px;
  height: 40px;
  margin-right: 4px;
  flex-shrink: 0;
`;

const LivePriceBar = styled.div`
  width: 100%;
  height: 34px; /* Reduced from 40px to 34px */
  background: ${props => props.theme.surface};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px; /* Changed from 8px 8px 0 0 to disconnect from chart */
  padding: 4px 16px; /* Reduced from 6px to 4px */
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px; /* Reduced from 12px to 10px */
  box-sizing: border-box;
`;

const LivePriceValue = styled.div<{ isBlinking?: boolean }>`
  font-size: 18px; /* Reduced from 20px to 18px */
  font-weight: bold;
  color: ${props => props.theme.primary};
  font-family: 'Courier New', monospace;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  
  ${props => props.isBlinking && `
    animation: priceBlink 0.6s ease-in-out;
    
    @keyframes priceBlink {
      0% { opacity: 1; transform: scale(1); }
      30% { opacity: 0.8; transform: scale(1.02); }
      100% { opacity: 1; transform: scale(1); }
    }
  `}
`;

const LiveIndicator = styled.span<{ isLive: boolean }>`
  background: ${props => props.isLive ? '#22C55E' : '#6B7280'};
  color: white;
  padding: 2px 6px; /* Reduced from 4px 8px to 2px 6px */
  border-radius: 10px; /* Reduced from 12px to 10px */
  font-size: 8px; /* Reduced from 10px to 8px */
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px ${props => props.isLive ? 'rgba(34, 197, 94, 0.3)' : 'rgba(107, 114, 128, 0.3)'};
  transition: all 0.3s ease;
  
  ${props => props.isLive && `
    animation: livePulse 3s infinite;
    
    @keyframes livePulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.7; }
    }
  `}
`;

const ChartContainer = styled.div`
  flex: 0 0 260px; /* Reduced from 280px for more space for predictions */
  width: 100%;
  background: ${props => props.theme.surface};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px 8px 0 0; /* Rounded top corners, connect to predictions list below */
  overflow: hidden;
  box-sizing: border-box;
  position: relative;
  margin-top: 4px; /* Add small gap between live price and chart */
  
  /* Add scroll zone markers */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 20px;
    background: linear-gradient(180deg, ${props => props.theme.surface}aa 0%, transparent 100%);
    pointer-events: none;
    z-index: 10;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 20px;
    background: linear-gradient(0deg, ${props => props.theme.surface}aa 0%, transparent 100%);
    pointer-events: none;
    z-index: 10;
  }
`;

interface MobileMainChartPageProps {
  activePredictions: number[];
  togglePrediction: (id: number) => void;
  chartData: Array<{ time: string; value: number }>;
  isDarkMode: boolean;
  theme: Theme;
  predictions: Prediction[];
  onRefresh?: () => Promise<void>; // Optional refresh callback
  onResetChart?: () => void; // Optional chart reset callback
  openModal?: () => void; // Optional modal opener for submit functionality
}

const MobileMainChartPage: React.FC<MobileMainChartPageProps> = ({
  activePredictions,
  togglePrediction,
  chartData,
  isDarkMode,
  theme,
  predictions,
  onRefresh,
  onResetChart,
  openModal
}) => {
  const location = useLocation();
  
  // Refs for scroll management
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Bottom nav state
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [showMenu, setShowMenu] = useState(false);
  
  // Influencer filter state
  const [selectedInfluencer, setSelectedInfluencer] = useState<string>('all');

  // Determine current asset from route
  const currentAsset = React.useMemo(() => {
    if (location.pathname.includes('/mstr')) return 'mstr';
    if (location.pathname.includes('/naka')) return 'naka';
    return 'btc'; // default
  }, [location.pathname]);

  // Route to MSTR-specific mobile component when on MSTR route
  if (currentAsset === 'mstr') {
    return (
      <MSTRMobileMainChartPage
        activePredictions={activePredictions}
        togglePrediction={togglePrediction}
        chartData={chartData}
        isDarkMode={isDarkMode}
        theme={theme}
        predictions={predictions}
        openModal={openModal}
      />
    );
  }

  // Default Bitcoin mobile chart (existing implementation)
  const handlePredictionClick = (predictionId: number) => {
    togglePrediction(predictionId);
  };

  // Bottom nav handlers
  const handleMenuClick = () => {
    setShowMenu(!showMenu);
  };

  const handleAssetSelect = (asset: 'btc' | 'mstr') => {
    setShowMenu(false);
    // Navigate to the selected asset
    if (asset === 'btc' && location.pathname !== '/btc') {
      window.location.href = '/btc';
    } else if (asset === 'mstr' && location.pathname !== '/mstr') {
      window.location.href = '/mstr';
    }
  };

  const handleResetChart = () => {
    
    // Reset local filter state
    setStatusFilter('all');
    
    // Call the parent's reset chart function to clear active predictions
    if (onResetChart) {
      onResetChart();
    } else {
      console.warn('⚠️ No onResetChart function provided');
    }
  };

  const handleStatusFilterChange = (filter: FilterStatus) => {
    setStatusFilter(filter);
  };

  // Handle influencer filter change
  const handleInfluencerChange = (influencer: string) => {
    setSelectedInfluencer(influencer);
  };

  // Extract unique influencers
  const uniqueInfluencers = React.useMemo(() => {
    const influencers = new Set<string>();
    predictions.forEach(pred => {
      if (pred.influencer?.name) {
        influencers.add(pred.influencer.name);
      }
    });
    return Array.from(influencers).sort();
  }, [predictions]);

  // Filter predictions based on status and influencer
  const filteredPredictions = React.useMemo(() => {
    let filtered = predictions;
    
    // Apply influencer filter
    if (selectedInfluencer !== 'all') {
      filtered = filtered.filter(prediction => 
        prediction.influencer?.name === selectedInfluencer
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(prediction => {
        switch (statusFilter) {
          case 'success':
            return prediction.success_status === 'success';
          case 'miss':
            return prediction.success_status === 'miss';
          case 'pending':
            return prediction.success_status === 'pending' || !prediction.success_status;
          case 'updated':
            return prediction.success_status === 'updated';
          default:
            return true;
        }
      });
    }
    
    return filtered;
  }, [predictions, statusFilter, selectedInfluencer]);

  // Count filtered predictions
  const filteredCount = React.useMemo(() => {
    if (statusFilter === 'all') return predictions.length;
    return filteredPredictions.length;
  }, [filteredPredictions, statusFilter, predictions]);

  // Use live price hook for real-time updates
  const { price: livePrice, loading: livePriceLoading, isBlinking } = useLivePrice();

  // Get current price from live data (preferred) or fall back to chart data
  const currentPrice = React.useMemo(() => {
    // Use live price if available
    if (livePrice !== null) return livePrice;
    
    // Fallback to latest chart data point
    if (!chartData || chartData.length === 0) return null;
    const latestPoint = chartData[chartData.length - 1];
    return latestPoint ? latestPoint.value : null;
  }, [livePrice, chartData]);

  // Determine if we're showing live data
  const isShowingLiveData = livePrice !== null && currentPrice === livePrice;

  // Format price with commas and proper currency
  const formatPrice = (price: number | null) => {
    if (price === null) return livePriceLoading ? 'Loading...' : '--';
    return `$${Math.round(price).toLocaleString()}`;
  };

  return (
    <MobileContainer ref={containerRef}>
      {/* Asset Selection Menu */}
      {showMenu && (
        <AssetSelectionOverlay>
          <AssetMenu theme={theme}>
            <AssetMenuHeader>Select Asset</AssetMenuHeader>
            <AssetOption 
              active={currentAsset === 'btc'}
              theme={theme}
              onClick={() => handleAssetSelect('btc')}
              style={{
                justifyContent: 'center'
              }}
            >
              <BitcoinIcon viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="12" fill="#F7931A"/>
                <text x="12" y="16" textAnchor="middle" fontSize="14" fontWeight="bold" fill="white" fontFamily="Arial, sans-serif" transform="rotate(8 12 16)">₿</text>
              </BitcoinIcon>
              Bitcoin (BTC)
            </AssetOption>
            <AssetOption 
              active={false}
              theme={theme}
              onClick={() => {}}
              style={{ 
                opacity: 1, 
                cursor: 'not-allowed',
                background: '#f8f8f8',
                color: '#666666',
                flexDirection: 'column',
                textAlign: 'center',
                gap: '4px'
              }}
              disabled
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                📈 Strategy (MSTR)
              </div>
              <div style={{ fontSize: '12px', color: '#999999' }}>
                Coming Soon
              </div>
            </AssetOption>
            <AssetMenuClose onClick={() => setShowMenu(false)}>
              Cancel
            </AssetMenuClose>
          </AssetMenu>
        </AssetSelectionOverlay>
      )}
      
      <LivePriceBar theme={theme}>
        <LivePriceValue theme={theme} isBlinking={isBlinking}>
          {formatPrice(currentPrice)}
        </LivePriceValue>
        <LiveIndicator isLive={isShowingLiveData}>
          {isShowingLiveData ? 'LIVE' : 'STATIC'}
        </LiveIndicator>
      </LivePriceBar>
      <ChartContainer>
        <MobileChartWrapper
          predictions={predictions}
          activePredictions={activePredictions}
          chartData={chartData}
          isDarkMode={isDarkMode}
          theme={theme}
        />
      </ChartContainer>
      
      <MobileInfluencerFilter
        theme={theme}
        selectedInfluencer={selectedInfluencer}
        uniqueInfluencers={uniqueInfluencers}
        onInfluencerChange={handleInfluencerChange}
        filteredCount={filteredPredictions.length}
        totalCount={predictions.length}
      />
      
      <MobilePredictionsList
        predictions={filteredPredictions}
        activePredictions={activePredictions}
        onPredictionClick={handlePredictionClick}
        openModal={openModal}
        currentAsset={currentAsset}
      />
      
      <BackToTopButton
        theme={theme}
        containerRef={containerRef}
        scrollThreshold={200}
      />
      
      <MobileBottomNav
        theme={theme}
        onMenuClick={handleMenuClick}
        onResetChart={handleResetChart}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        filteredCount={filteredCount}
      />
    </MobileContainer>
  );
};

export default MobileMainChartPage;